import { useCallback } from "react";
import { format } from "date-fns";
import {
  createUserApi,
  CreateUserRequestBody,
  getSensorReferenceApi,
  GetSensorReferenceResponse,
  getSiteStateApi,
  getSitesApi,
  GetSitesResponse,
  getUsersApi,
  GetUsersResponse,
  RokaApiErrorResponse,
  setSiteStateApi,
  SetSiteStateRequestBody,
  updateSensorReferenceApi,
  updateSiteApi,
  updateUserApi,
  updateUserPasswordApi,
  UpdateUserRequestBody,
} from "../../api/adminApi";
import { GetStateResponse } from "../../api/userStateApi";
import { RokaApiError } from "../../errors/RokaApiError";
import { SensorReference, Site, User } from "../../types/rokaTypes";
import { useFetchWithToken } from "../utility-hoks/useFetchWithToken";

export const useAdminApi = () => {
  const { fetchWithToken } = useFetchWithToken();

  const fetchUsers = useCallback(async (): Promise<User[]> => {
    const response = await fetchWithToken(getUsersApi());
    const parsedResponse = (await response.json()) as GetUsersResponse;
    return parsedResponse.users;
  }, [fetchWithToken]);

  const createUser = useCallback(
    async (data: CreateUserRequestBody): Promise<void> => {
      const response = await fetchWithToken(createUserApi(data));
      if (!response.ok) {
        const errorResponse = (await response.json()) as RokaApiErrorResponse;
        throw new RokaApiError(errorResponse);
      } else if (response.status === 200) {
        return;
      } else {
        throw new Error("Failed to create user");
      }
    },
    [fetchWithToken],
  );

  const updateUser = useCallback(
    async (username: string, data: UpdateUserRequestBody): Promise<void> => {
      const response = await fetchWithToken(updateUserApi(username, data));
      if (!response.ok) {
        const errorResponse = (await response.json()) as RokaApiErrorResponse;
        throw new RokaApiError(errorResponse);
      } else if (response.status === 200) {
        return;
      } else {
        throw new Error("Failed to update user");
      }
    },
    [fetchWithToken],
  );

  const updateUserPassword = useCallback(
    async (username: string, password: string): Promise<void> => {
      const response = await fetchWithToken(
        updateUserPasswordApi(username, { password }),
      );
      if (!response.ok) {
        const errorResponse = (await response.json()) as RokaApiErrorResponse;
        throw new RokaApiError(errorResponse);
      } else if (response.status === 200) {
        return;
      } else {
        throw new Error("Failed to update user password");
      }
    },
    [fetchWithToken],
  );

  const fetchSites = useCallback(async (): Promise<Site[]> => {
    const response = await fetchWithToken(getSitesApi());
    const parsedResponse = (await response.json()) as GetSitesResponse;

    // Parse dates
    const sites: Site[] = parsedResponse.sites.map((site) => ({
      ...site,
      eventDate: new Date(site.eventDate),
      updated: site.updated ? new Date(site.updated) : null,
      sensors: site.sensors.map((sensor) => ({
        ...sensor,
        updated: sensor.updated ? new Date(sensor.updated) : null,
      })),
    }));

    return sites;
  }, [fetchWithToken]);

  const updateSite = useCallback(
    async (name: string, maximum: number): Promise<void> => {
      const response = await fetchWithToken(updateSiteApi(name, { maximum }));
      if (!response.ok) {
        const errorResponse = (await response.json()) as RokaApiErrorResponse;
        throw new RokaApiError(errorResponse);
      } else if (response.status === 200) {
        return;
      } else {
        throw new Error("Failed to update site");
      }
    },
    [fetchWithToken],
  );

  const fetchSensorReference = useCallback(
    async (siteName: string, sensorName: string): Promise<SensorReference> => {
      const response = await fetchWithToken(
        getSensorReferenceApi(siteName, sensorName),
      );
      const parsed = (await response.json()) as GetSensorReferenceResponse;
      return {
        currentRaw: parsed.current_raw,
        currentRawReference: parsed.current_raw_reference,
        current: parsed.current,
      };
    },
    [fetchWithToken],
  );

  const updateSensorReference = useCallback(
    async (
      siteName: string,
      sensorName: string,
      reference?: number,
    ): Promise<{ currentRawReference: number; current: number }> => {
      const body = reference !== undefined ? { reference } : {};
      const response = await fetchWithToken(
        updateSensorReferenceApi(siteName, sensorName, body),
      );
      if (!response.ok) {
        const errorResponse = (await response.json()) as RokaApiErrorResponse;
        throw new RokaApiError(errorResponse);
      }
      const parsed = (await response.json()) as {
        current_raw_reference: number;
        current: number;
      };
      return {
        currentRawReference: parsed.current_raw_reference,
        current: parsed.current,
      };
    },
    [fetchWithToken],
  );

  const fetchSiteResetTimes = useCallback(
    async (
      siteName: string,
    ): Promise<{ currentDay: string; default: string }> => {
      const [todayRes, defaultRes] = await Promise.all([
        fetchWithToken(getSiteStateApi(siteName)),
        fetchWithToken(getSiteStateApi(`${siteName}#default`)),
      ]);
      const today = (await todayRes.json()) as GetStateResponse;
      const def = (await defaultRes.json()) as GetStateResponse;
      const toHHmm = (s: string) => String(s).substring(11, 16) || "04:00";
      return {
        currentDay: toHHmm(today.body.reset_time as unknown as string),
        default: toHHmm(def.body.reset_time as unknown as string),
      };
    },
    [fetchWithToken],
  );

  const updateSiteResetTime = useCallback(
    async (
      siteName: string,
      time: string,
      isDefault: boolean,
    ): Promise<void> => {
      const today = format(new Date(), "yyyy-MM-dd");
      const body: SetSiteStateRequestBody = isDefault
        ? { name: `${siteName}#default`, reset_time: `${time}:00` }
        : { name: siteName, reset_time: `${today}T${time}:00` };
      const response = await fetchWithToken(setSiteStateApi(body));
      if (!response.ok) {
        const errorResponse = (await response.json()) as RokaApiErrorResponse;
        throw new RokaApiError(errorResponse);
      }
    },
    [fetchWithToken],
  );

  return {
    fetchUsers,
    createUser,
    updateUser,
    updateUserPassword,
    fetchSites,
    updateSite,
    fetchSensorReference,
    updateSensorReference,
    fetchSiteResetTimes,
    updateSiteResetTime,
  };
};
